import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, Copy, Inbox, Plus, XCircle } from 'lucide-react';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { formatDate } from '../../utils/format';
import { useTenant } from '../../hooks/useTenant';
import { ACTIONS } from '../tenancy/actions';
import CreateFileRequestModal from './CreateFileRequestModal';
import {
  closeFileRequest,
  fileRequestKeys,
  listFileRequests,
  requestLink,
} from './fileRequestApi';

export default function FileRequestsPage() {
  const { allows } = useTenant();
  const canCreate = allows(ACTIONS.FILE_REQUEST_CREATE);
  const canManage = allows(ACTIONS.FILE_REQUEST_MANAGE);
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState(null); // { token, title } shown once
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: fileRequestKeys.list(),
    queryFn: () => listFileRequests(),
  });

  const closeMutation = useMutation({
    mutationFn: (id) => closeFileRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['file-requests'] });
      toast.success('Request closed');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not close the request')),
  });

  const rows = data?.data ?? [];

  const copyLink = async () => {
    await navigator.clipboard.writeText(requestLink(created.token));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-brand-600" />
          <h1 className="text-xl font-semibold text-slate-800">File requests</h1>
        </div>
        {canCreate && <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New request
        </Button>}
      </div>

      {created && (
        <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-medium text-emerald-800">
            Request created — share this link to collect files:
          </p>
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-slate-100 p-2">
            <input
              readOnly
              value={requestLink(created.token)}
              className="min-w-0 flex-1 bg-transparent px-2 text-sm text-slate-700 focus:outline-none"
            />
            <Button size="sm" variant="secondary" onClick={copyLink}>
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-emerald-700">
            {created.recipientEmail
              ? `A link was emailed to ${created.recipientEmail}. It is also shown once here — copy it if you need it.`
              : 'This link is shown once — copy it now.'}
          </p>
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <p className="text-sm text-red-600">{getErrorMessage(error, 'Could not load requests')}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          No file requests yet. Create one to start collecting files.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Files</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="text-slate-700">
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3">
                    {r.submissionCount}
                    {r.maxFiles ? ` / ${r.maxFiles}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        r.status === 'open'
                          ? 'inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700'
                          : 'inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500'
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{r.expiresAt ? formatDate(r.expiresAt) : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {canManage && r.status === 'open' && (
                      <button
                        onClick={() => closeMutation.mutate(r.id)}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50"
                        title="Close request"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        Close
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateFileRequestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(data) => {
          setCreated(data);
          setCopied(false);
          queryClient.invalidateQueries({ queryKey: ['file-requests'] });
        }}
      />
    </div>
  );
}
